import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { isPast, isFuture } from 'date-fns'
import { useFlexFlagIsOn } from '@indeed/flex-feature-flags'

import { Button, ButtonDropdown, MenuItem } from 'syft-acp-atoms/Button'
import { showModal } from 'syft-acp-core/store/modals/actions'
import ReactTooltip from 'react-tooltip'

import { getModal$ } from 'syft-acp-core/store/modals/selectors'

export const ListingShiftCancelButton = ({ dispatch, shiftID, shiftBooking, jobs }) => {
  const { worker, shift, jobID, long_term_assignment } = shiftBooking

  const job = jobs?.entityDetail?.[jobID]
  const futureShifts = (job?.shifts || []).filter(s => isFuture(Date.parse(s.start_time)))

  // A BLOCK BOOKING is a job whose shifts are booked together — the backend's own
  // definition is bookable_individually === false (see flex-core cancel_booking.rb's
  // MustCancelAllBookedShiftsValidator). Use STRICT === false so a missing/undefined
  // job (jobs not yet in the store) safely means "not a block booking" rather than
  // asserting one via !truthy.
  const isBlockBooking = job?.bookable_individually === false

  // The bulk dialog fetches the worker's booked shifts itself (from the backend), so we
  // only pass identity + the clicked shift + the block-booking signal here. It covers
  // single-shift cancellation too — the clicked shift is pre-ticked. jobShiftCount is the
  // JOB's total shift count (not the worker's bookings): flex-core only promotes an
  // overbook backup onto a vacated spot for single-shift jobs (CancelBooking
  // #promote_overbook_workers: `return unless job.shifts.one?`), so the dialog needs it to
  // know whether promotion can actually happen. Undefined when the job isn't in the store
  // yet, which the dialog treats as "not eligible" rather than promising a promotion.
  const handleCancelClick = () => {
    dispatch(
      showModal('bulkCancelBookingModal', {
        worker,
        jobID,
        currentShiftID: shiftID,
        isBlockBooking,
        jobShiftCount: job?.shifts?.length,
      }),
    )
  }

  const shiftIds = futureShifts.map(s => s.id)
  const openEndAssignmentModal = () => {
    dispatch(
      showModal('EndJobAssignmentModal', {
        worker,
        jobID,
        shiftIds,
        longTermAssignment: long_term_assignment,
      }),
    )
  }

  const pastShift = isPast(Date.parse(shift?.start_time))
  const endAssignmentFlag = useFlexFlagIsOn('pte_13454_end_job_assignment')
  const disabledTermination = pastShift || !long_term_assignment

  return !endAssignmentFlag || disabledTermination ? (
    <div className="small-padded">
      <Button onClick={handleCancelClick} block>
        Cancel
      </Button>
    </div>
  ) : (
    <div style={{ position: 'absolute' }} className="small-padded">
      <ButtonDropdown title="Cancel" id="dropdown_actions" pullRight>
        <MenuItem
          data-tip="Cancel worker from this individual shift"
          data-for="cancel-tooltip"
          onClick={handleCancelClick}
          key="1"
        >
          Cancel shift
        </MenuItem>
        <MenuItem
          data-tip="End worker's long term assignment"
          data-for="cancel-tooltip"
          key="2"
          onClick={openEndAssignmentModal}
        >
          End assignment
        </MenuItem>
      </ButtonDropdown>
      <ReactTooltip effect="solid" id="cancel-tooltip" />
    </div>
  )
}

ListingShiftCancelButton.propTypes = {
  dispatch: PropTypes.func.isRequired,
  shiftID: PropTypes.number.isRequired,
  shiftBooking: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    worker: PropTypes.object,
  }).isRequired,
}

export default connect(state => ({
  item: getModal$(state.modals)('EndJobAssignmentModal'),
  jobs: state?.jobs,
}))(ListingShiftCancelButton)
